import React, { useEffect, useMemo } from "react";
import useReport from "../hooks/useReport";
import useSummaryData from "../hooks/useSummaryData";
import { useParams } from "react-router-dom";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import SummaryAccordion, { AccordionRow } from "./summaryAccordion";
import useEcomodClasses from "../hooks/useEcomodClasses";
import useSupClasses from "../hooks/useSupClasses";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import usePrices from "../hooks/usePrices";
import useReports from "../hooks/useReports";
import {
  createSummaryData,
  getChangeErrors,
  getDivideAmount,
  getMatchingReport,
  getMultiplyAmount,
  getReportingCycle,
} from "../utils/summaryUtils";
import { ReportStatus } from "../api/models/report.models";
import { Report } from "../api/models/report.models";
import ChangeErrorDisplay from "./changeErrorDisplay";

const PaddedContainer = styled.div`
  padding: 2rem 2rem 6rem 2rem;
  text-align: left;
`;

const SectionCard = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const TotalCostContainer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #d3d3d3;

  & .section-title {
    font-size: 14px;
    text-transform: uppercase;
    font-weight: 600;
    color: var(--text-color-secondary);
    margin-bottom: 0.5rem;
  }

  & .amount {
    font-size: 18px;
    font-weight: 600;
  }

  & .notification {
    font-size: 14px;
    color: var(--text-color-secondary);
    font-weight: 500;
    margin-top: 0.5rem;
  }
`;

interface SummaryTableProps {
  reportComment: string;
  setReportComment: (comment: string) => void;
  forceComment?: boolean;
  setForceComment?: (force: boolean) => void;
  setIsEmptyReport?: (isEmpty: boolean) => void;
}

export default function SummaryTable({
  reportComment,
  setReportComment,
  forceComment,
  setForceComment,
  setIsEmptyReport,
}: SummaryTableProps) {
  const { reportId } = useParams();
  const { t } = useTranslation();
  const [locale, companyId, organizationId] = useUIStore(
    useShallow((state: UIState) => [
      state.locale,
      state.companyId,
      state.organizationId,
    ]),
  );

  const { reportsData, isLoadingReports } = useReports({
    companyId,
    organizationId,
    withRows: true,
  });

  const { reportData, isLoadingReport } = useReport({
    reportId: parseInt(reportId as string, 10),
    disableCache: true,
  });

  const reportType = reportData?.reportType;

  // Find all reports from previous year, they might be yearly, quarterly or monthly reports
  const prevYearReports = reportData?.year
    ? reportsData?.filter(
        (report) =>
          report.year === reportData?.year - 1 &&
          (report.reportStatus === ReportStatus.Sent ||
            report.reportStatus === ReportStatus.WaitingForApproval) &&
          report.id !== reportData.id,
      )
    : null;

  // Get previous year same quarter report or yearly report
  const prevYearReport = getMatchingReport(prevYearReports, reportData);

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: reportData ? reportData.year : new Date().getFullYear(),
  });

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    consumerBusinessCost,
    supCost,
  } = useSummaryData({
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  });

  const isLoading =
    isLoadingReport ||
    isLoadingEcomodClasses ||
    isLoadingSupClasses ||
    isLoadingPrices ||
    isLoadingReports;

  const currentYearReportingCycle = getReportingCycle(reportData as Report);

  const prevYearReportingCycle = getReportingCycle(prevYearReport as Report);

  const dividePreviousYearBy = getDivideAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const multiplyPreviousYearBy = getMultiplyAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const changeErrors = useMemo(() => {
    return getChangeErrors(
      consumerData || [],
      dividePreviousYearBy,
      multiplyPreviousYearBy,
      prevYearConsumerData,
      t("label-consumer"),
    )
      .concat(
        getChangeErrors(
          businessData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearBusinessData,
          t("label-business"),
        ),
      )
      .concat(
        getChangeErrors(
          supData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearSupData,
          t("label-single-use-plastic"),
        ),
      );
  }, [
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    dividePreviousYearBy,
    multiplyPreviousYearBy,
    t,
  ]);

  // A report with no reported weight at all (e.g. every material left at 0) has nothing
  // for getChangeErrors to compare against, so it must be flagged separately.
  const isEmptyReport = useMemo(() => {
    if (isLoading || !consumerData || !businessData || !supData) {
      return false;
    }

    const totalWeight = [...consumerData, ...businessData, ...supData].reduce(
      (sum, row) => sum + (row.combinedWeight || 0),
      0,
    );

    return totalWeight === 0;
  }, [isLoading, consumerData, businessData, supData]);

  // Watch for changes in errors or an empty report to force comment
  useEffect(() => {
    if (setForceComment) {
      if ((changeErrors && changeErrors.length > 0) || isEmptyReport) {
        setForceComment(true);
      } else if (forceComment) {
        setForceComment(false);
      }
    }
  }, [changeErrors, isEmptyReport, forceComment, setForceComment]);

  useEffect(() => {
    setIsEmptyReport?.(isEmptyReport);
  }, [isEmptyReport, setIsEmptyReport]);

  const hasConsumerData =
    !!consumerData?.length &&
    consumerData.some((row) => row.combinedWeight > 0);
  const hasBusinessData =
    !!businessData?.length &&
    businessData.some((row) => row.combinedWeight > 0);
  const hasSupData =
    !!supData?.length && supData.some((row) => row.combinedWeight > 0);

  return (
    <div className="card">
      <PaddedContainer>
        <LoadingContainer
          status={
            isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
          }
        >
          {(hasConsumerData || hasBusinessData) && (
            <SectionCard>
              {hasConsumerData && (
                <SummaryAccordion
                  tableData={consumerData || []}
                  header={t("label-consumer")}
                  prevYearData={prevYearConsumerData}
                  prevYearReportingCycle={prevYearReportingCycle}
                  currentYearReportingCycle={currentYearReportingCycle}
                />
              )}
              {hasBusinessData && (
                <SummaryAccordion
                  tableData={businessData || []}
                  header={t("label-business")}
                  prevYearData={prevYearBusinessData}
                  prevYearReportingCycle={prevYearReportingCycle}
                  currentYearReportingCycle={currentYearReportingCycle}
                />
              )}

              <TotalCostContainer>
                <div className="section-title">{t("label-recycling-fees")}</div>
                <div className="amount">
                  {`${t("label-total-cost")}: ${consumerBusinessCost} ${consumerBusinessCost ? "€" : ""}`}
                  {`${consumerBusinessCost ? ` ${t("label-vat-amount")}` : ""}`}
                </div>
                <div className="notification">
                  {t("label-total-amount-notification")}
                </div>
              </TotalCostContainer>
            </SectionCard>
          )}

          {hasSupData && (
            <SectionCard>
              <SummaryAccordion
                tableData={supData || []}
                header={t("label-single-use-plastic")}
                prevYearData={prevYearSupData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                reportType={reportType}
              />

              <TotalCostContainer>
                <div className="section-title">
                  {t("label-single-use-plastic")}
                </div>
                <div className="amount">
                  {`${t("label-total-cost")}: ${supCost} ${supCost ? "€" : ""}`}
                  {`${supCost ? ` ${t("label-vat-amount")}` : ""}`}
                </div>
              </TotalCostContainer>
            </SectionCard>
          )}

          <div style={{ paddingTop: "2rem" }}>
            <ChangeErrorDisplay
              errors={changeErrors || []}
              isEmptyReport={isEmptyReport}
              changeString={reportComment}
              onChangeStringUpdate={setReportComment}
            />
          </div>
        </LoadingContainer>
      </PaddedContainer>
    </div>
  );
}
